<template>
  <section class="lg:max-w-[600px] lg:mx-auto">
    <StatusBar v-if="!isDesktopFlow" />
    <section :class="{ 'px-4': !isDesktopFlow }">
      <h1 class="text-2xl md:text-4xl font-bold font-heading mb-8">Checkout</h1>
      <template v-if="contactInfoSaved">
        <p>{{ contact.first_name }} {{ contact.last_name }}</p>
        <p>{{ contact.email }}</p>
        <p>{{ contact.phone }}</p>
        <button
          id="contact-info-summary"
          class="text-button-guest-primary underline mt-2 inline-block"
          @click="contactInfoSaved = false"
        >
          Edit
        </button>
        <div v-if="isStripe" class="my-6" id="payment-element" />
        <div
          v-if="error.message"
          id="payment-error"
          class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
        >
          {{ error.message }}
        </div>
      </template>
      <form v-else @submit.prevent class="mb-8">
        <div class="flex flex-col space-y-2 xs:flex-row xs:space-y-0">
          <TextInput
            id="fname"
            forInput="fname-input"
            v-model="contact.first_name"
            label="First Name"
            classes="w-full xs:w-1/2 xs:pr-2"
            placeholder="First Name"
          />
          <TextInput
            id="lname"
            forInput="lname-input"
            v-model="contact.last_name"
            label="Last Name"
            classes="w-full xs:w-1/2"
            placeholder="Last Name"
          />
        </div>
        <div class="flex flex-col space-y-2 mt-4 xs:flex-row xs:space-y-0 mb-8">
          <div class="w-full xs:w-3/5 xs:pr-2">
            <TextInput
              id="email"
              forInput="email-input"
              type="email"
              v-model="contact.email"
              label="Email"
              placeholder="Email"
              :inputClasses="`transition duration-200 ease-in-out ${
                emailError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
              }`"
              @blur="handleEmailBlur"
              @update:modelValue="handleEmailUpdate"
            />
            <p v-if="emailError" class="text-[#df1b41] text-sm mt-0.5" id="email-error-message">
              {{ emailError }}
            </p>
          </div>
          <div class="w-full xs:w-2/5">
            <TextInput
              id="tel"
              forInput="tel-input"
              type="tel"
              v-model="contact.phone"
              label="Phone"
              placeholder="Phone"
              classes="w-full"
              :inputClasses="`transition duration-200 ease-in-out ${
                phoneError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
              }`"
              @blur="handlePhoneBlur"
              @update:modelValue="handlePhoneUpdate"
            />
            <p v-if="phoneError" class="text-[#df1b41] text-sm mt-0.5" id="phone-error-message">
              {{ phoneError }}
            </p>
          </div>
        </div>

        <div
          v-if="error.message"
          id="error-display"
          class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
        >
          {{ error.message }}
        </div>

        <button
          v-if="requiresPayment"
          id="enter-payment-button"
          class="w-full md:w-1/2 px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
          :disabled="!isContactFormValid || isSaving"
          @click="saveContactAndShowPayment"
        >
          {{ enterPaymentButtonText }}
        </button>
      </form>
      <div v-if="!isDesktopFlow" class="price-information">
        <CheckoutDetailTable />
        <CheckoutPromoCode class="my-8" />
      </div>
      <template v-if="requiresPayment ? contactInfoSaved : true">
        <CheckboxInput
          id="accepts-marketing"
          v-model="contact.accepts_marketing"
          :label="`Keep me informed of upcoming ${settingStore.venue_name} shows`"
          guest
        />
        <div v-if="checkout_confirmation_text" class="mt-7">
          <CheckboxInput
            id="accepts-checkout-confirmation"
            v-model="acceptsCheckoutConfirmation"
            label="I agree to the following policy"
            guest
          />
          <div
            class="text-sm text-fg-g-primary mt-2 whitespace-pre-line"
            id="checkout-confirmation-text"
          >
            {{ checkout_confirmation_text }}
          </div>
        </div>
      </template>
      <p v-show="checkout_text" class="text-sm text-fg-g-primary mt-7">
        {{ checkout_text }}
      </p>
    </section>
    <CheckoutBottomBar
      @click:next="handleBottomBarClick"
      :isNextButtonDisabled="isBottomBarButtonDisabled"
      :nextButtonText="bottomBarButtonText"
    />
  </section>
</template>

<script setup lang="ts">
import type {
  Appearance,
  PaymentIntentResult,
  Stripe,
  StripeElements,
  StripeElementsOptionsClientSecret,
} from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useCssVar } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref } from 'vue'
import { z } from 'zod'

import CheckboxInput from '@/common/components/molecules/CheckboxInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import CheckoutDetailTable from '@/modules/guestUi/components/organisms/CheckoutDetailTable.vue'
import CheckoutPromoCode from '@/modules/guestUi/components/organisms/CheckoutPromoCode.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const settingStore = useSettingStore()
const { checkout_confirmation_text, checkout_text, public_key, stripe_account } =
  storeToRefs(settingStore)

const checkoutStore = useCheckoutStore()
const {
  hasSufficientGuestInfo,
  contact,
  isDesktopFlow,
  requiresPayment,
  client_secret,
  is_timer_expired,
  pricing,
} = storeToRefs(checkoutStore)

const acceptsCheckoutConfirmation = ref<boolean>(false)
const contactInfoSaved = ref<boolean>(false)
const isSaving = ref<boolean>(false)
const isPaying = ref<boolean>(false)
const error = ref<ErrorData>(blankError())
const isStripeFormComplete = ref<boolean>(false)

// Email validation
const emailSchema = z.string().email({ message: 'Please enter a valid email address.' })
const emailError = ref<string>('')
const isEmailValid = computed<boolean>(() => emailError.value === '')
function validateEmail(email: string): void {
  const result = emailSchema.safeParse(email)
  if (!result.success) emailError.value = result.error.issues[0].message
  else emailError.value = ''
}
onMounted(handleEmailBlur)
function handleEmailBlur(): void {
  if (contact.value.email.length > 0) validateEmail(contact.value.email)
  else emailError.value = ''
}
function handleEmailUpdate(email: string): void {
  if (email.length === 0) emailError.value = ''
}

// Phone validation
const phoneSchema = z
  .string()
  .max(20, { message: 'Please ensure this field has no more than 20 characters.' })
const phoneError = ref<string>('')
const isPhoneValid = computed<boolean>(() => phoneError.value === '')
function validatePhone(phone: string): void {
  const result = phoneSchema.safeParse(phone)
  if (!result.success) phoneError.value = result.error.issues[0].message
  else phoneError.value = ''
}
onMounted(handlePhoneBlur)
function handlePhoneBlur(): void {
  if (contact.value.phone.length > 0) validatePhone(contact.value.phone)
  else phoneError.value = ''
}
function handlePhoneUpdate(phone: string): void {
  if (phone.length === 0) phoneError.value = ''
}

const isContactFormValid = computed<boolean>(
  () => hasSufficientGuestInfo.value && isEmailValid.value && isPhoneValid.value,
)
const isFormValid = computed<boolean>(() => {
  return (
    (!checkout_confirmation_text.value || acceptsCheckoutConfirmation.value) &&
    isContactFormValid.value
  )
})

const enterPaymentButtonText = computed<string>(() => {
  return isSaving.value ? 'LOADING...' : 'ENTER PAYMENT'
})

async function saveContactAndShowPayment(): Promise<void> {
  if (!isContactFormValid.value || isSaving.value) return
  isSaving.value = true
  clearError(error.value)
  try {
    await checkoutStore.setContactInfo()
    contactInfoSaved.value = true
    nextTick(loadStripeForm)
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isSaving.value = false
  }
}

const GUEST_UI_QUERY = '.guest-ui'
const guestUIContainer = ref<HTMLDivElement | null>(null)
onMounted(() => {
  guestUIContainer.value = document.querySelector(GUEST_UI_QUERY)
})
const brandColor = useCssVar('--primary-theme-color')
const textColorOnBrand = useCssVar('--colors-button-label', guestUIContainer)
const inputBgColor = useCssVar('--colors-input', guestUIContainer)
const inputTextColor = useCssVar('--colors-fg-g-primary', guestUIContainer)
const labelTextColor = useCssVar('--colors-fg-disclaimer', guestUIContainer)
const placeholderTextColor = useCssVar('--color-gray-60')
const primaryTextFontFamily = useCssVar('--fontFamily-sans', guestUIContainer)
const primaryTextFontFamilyUrl = useCssVar('--primary-text-font-family-url')
const borderColor = useCssVar('--colors-linear-g-primary', guestUIContainer)
const buttonStyle = { boxShadow: 'none', borderColor: borderColor.value, fontWeight: 'normal' }
const focusBorderColor = useCssVar('--g-fg-primary', null, { initialValue: 'black' })
const focusStyle = {
  boxShadow: `0 0 0 3px ${focusBorderColor.value}`,
  borderColor: 'transparent',
}
const elementsOptions = computed<StripeElementsOptionsClientSecret>(() => {
  return {
    clientSecret: client_secret.value!,
    fonts: [{ src: primaryTextFontFamilyUrl.value || '' }],
    appearance: {
      variables: {
        colorPrimary: brandColor.value,
        colorBackground: inputBgColor.value,
        colorText: inputTextColor.value,
        colorTextPlaceholder: `hsl(${placeholderTextColor.value})`,
        borderRadius: '4px',
        fontFamily: primaryTextFontFamily.value,
      },
      rules: {
        '.Tab': buttonStyle,
        '.Tab:focus': focusStyle,
        '.Tab--selected': {
          backgroundColor: brandColor.value,
          color: textColorOnBrand.value,
          borderColor: brandColor.value,
        },
        '.Tab--selected:focus': focusStyle,
        '.TabIcon--selected': { fill: textColorOnBrand.value },
        '.Input': buttonStyle,
        '.Input:focus': focusStyle,
        '.Label': { color: labelTextColor.value },
      },
    } as Appearance,
  }
})

const stripe = ref<Stripe | null>(null)
const elements = ref<StripeElements | null>(null)
const isStripe = computed<boolean>(() => {
  return pricing.value.total > 0.5 && public_key.value !== 'pk_test_xxx'
})

async function loadStripeForm() {
  await settingStore.loading
  // We want to avoid attempting to load stripe when we know we are running a test
  if (!isStripe.value) return

  stripe.value = await loadStripe(public_key.value, { stripeAccount: stripe_account.value })
  if (!stripe.value) return

  elements.value = stripe.value.elements(elementsOptions.value)
  if (!elements.value) return
  const paymentElement = elements.value.create('payment')
  paymentElement.on('change', event => {
    isStripeFormComplete.value = event.complete
    if (event.error) {
      error.value.message = event.error.message
    } else {
      clearError(error.value)
    }
  })
  paymentElement.mount('#payment-element')
}

async function submitPayment() {
  if (is_timer_expired.value) return
  try {
    isPaying.value = true
    clearError(error.value)

    let stripeResponse: PaymentIntentResult | undefined
    if (checkoutStore.requiresPayment) {
      await elements.value?.submit()
      stripeResponse = await stripe.value?.confirmPayment({
        elements: elements.value!,
        clientSecret: client_secret.value!,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      })
    }
    await checkoutStore.checkout(stripeResponse)
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isPaying.value = false
  }
}
const isBottomBarButtonDisabled = computed<boolean>(() => {
  if (!requiresPayment.value) {
    return isSaving.value || !isFormValid.value
  }
  if (contactInfoSaved.value) {
    return isPaying.value || !isFormValid.value || !isStripeFormComplete.value
  }
  return true
})

const bottomBarButtonText = computed<string>(() => {
  if (contactInfoSaved.value) {
    return isPaying.value ? 'Processing...' : 'Purchase'
  }
  if (requiresPayment.value) {
    return 'Purchase'
  } else {
    return isSaving.value ? 'Reserving...' : 'Reserve'
  }
})

async function handleBottomBarClick(): Promise<void> {
  if (isBottomBarButtonDisabled.value) return

  if (contactInfoSaved.value) {
    await submitPayment()
    return
  }

  if (!requiresPayment.value) {
    isSaving.value = true
    clearError(error.value)
    try {
      await checkoutStore.setContactInfo()
      await checkoutStore.checkout()
    } catch (e) {
      displayHandled(e, error.value)
    } finally {
      isSaving.value = false
    }
  }
}
</script>
